import React, {
  createContext,
  useContext,
  useReducer,
  useState,
  ReactNode,
} from "react";
import {
  Box,
  Stack,
  Button,
  IconButton,
  Chip,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  CardActionArea,
  Tooltip,
} from "@mui/material";

import { Close as CloseIcon, Delete as DeleteIcon } from "@mui/icons-material";

// Type definitions
interface ApiError {
  id: number;
  message: string;
  status?: number;
  endpoint?: string;
  method?: string;
  error?: object;
  type: string;
  key?: number;
  timestamp: string;
}

interface ErrorState {
  errors: ApiError[];
}

interface ErrorContextValue extends ErrorState {
  addError: (errorData: Omit<ApiError, "id" | "timestamp">) => void;
  removeError: (id: number) => void;
  clearErrors: () => void;
  getError: (id: number) => ApiError | undefined;
}

type ErrorAction =
  | { type: "ADD_ERROR"; payload: Omit<ApiError, "id" | "timestamp"> }
  | { type: "REMOVE_ERROR"; payload: { id: number } }
  | { type: "CLEAR_ERRORS" };

// Default context value
const defaultErrorContext: ErrorContextValue = {
  errors: [],
  addError: () => {
    console.warn("addError called outside of ErrorProvider");
  },
  removeError: () => {
    console.warn("removeError called outside of ErrorProvider");
  },
  clearErrors: () => {
    console.warn("clearErrors called outside of ErrorProvider");
  },
  getError: () => {
    console.warn("getError called outside of ErrorProvider");
    return undefined;
  },
};

// Error Context Setup
const ErrorContext = createContext<ErrorContextValue>(defaultErrorContext);

const errorReducer = (state: ErrorState, action: ErrorAction): ErrorState => {
  switch (action.type) {
    case "ADD_ERROR":
      console.log("ADDING ERROR", Date.now(), action.payload);
      return {
        ...state,
        errors: [
          ...state.errors,
          {
            id: action.payload.key
              ? action.payload.key
              : Date.now() + Math.random(), // Ensure unique ID
            timestamp: new Date().toISOString(),
            ...action.payload,
          },
        ],
      };
    case "REMOVE_ERROR":
      return {
        ...state,
        errors: state.errors.filter((error) => error.id !== action.payload.id),
      };
    case "CLEAR_ERRORS":
      return { ...state, errors: [] };
    default:
      return state;
  }
};

// Error Provider Component
interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(errorReducer, { errors: [] });

  const addError = (errorData: Omit<ApiError, "id" | "timestamp">): void => {
    dispatch({ type: "ADD_ERROR", payload: errorData });
  };

  const removeError = (id: number): void => {
    dispatch({ type: "REMOVE_ERROR", payload: { id } });
  };

  const clearErrors = (): void => {
    dispatch({ type: "CLEAR_ERRORS" });
  };

  const getError = (id: number): ApiError | undefined => {
    return state.errors.find((error) => error.id === id);
  };

  const contextValue: ErrorContextValue = {
    errors: state.errors,
    addError,
    removeError,
    clearErrors,
    getError,
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

// Custom hook to use the error context
export const useError = (): ErrorContextValue => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useError must be used within ErrorProvider");
  }
  return context;
};

// Component to display all errors from the context
export const ErrorDisplay: React.FC = () => {
  const { errors, removeError, clearErrors } = useError();

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (
    status?: number
  ): "default" | "error" | "warning" | "info" | "primary" => {
    if (!status) return "default";
    if (status >= 500) return "error";
    if (status >= 400) return "warning";
    if (status === 0) return "info";
    return "primary";
  };

  const getMethodColor = (
    method: string
  ): "default" | "primary" | "warning" | "info" => {
    switch (method) {
      case "POST":
        return "info";

      case "GET":
        return "primary";

      case "PUT":
        return "default";

      case "DELETE":
        return "warning";

      default:
        return "default";
    }
  };

  return (
    <Stack
      sx={{
        direction: "column",
        minWidth: "md",
        maxWidth: "md",
        p: 2,
        border: "1px solid black",
        borderRadius: 5,
        gap: 2,
      }}
    >
      <Typography variant="h2">Error Log: ({errors.length} errors)</Typography>
      {errors.length > 0 && (
        <Button onClick={clearErrors} variant="outlined" color="error">
          Clear All Errors
        </Button>
      )}

      {errors.length === 0 ? (
        <Typography variant="body1">No errors logged yet.</Typography>
      ) : (
        <Stack
          sx={{
            direction: "column",
            width: "100%",
            gap: 2,
          }}
        >
          {errors.map((error: ApiError) => (
            <Card key={error.id}>
              <CardContent sx={{ height: "100%", width: "100%" }}>
                <Stack
                  sx={{
                    direction: "column",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Chip
                        label={error.status || "N/A"}
                        clickable={false}
                        onClick={() => {}}
                        color={getStatusColor(error.status)}
                      />
                      {error.method && (
                        <Chip
                          label={error.method.toUpperCase()}
                          clickable={false}
                          onClick={() => {}}
                          color={getMethodColor(error.method.toUpperCase())}
                        />
                      )}
                      <Typography variant="body2">
                        {error.endpoint || ""}
                      </Typography>
                    </Box>
                    <Tooltip title="Remove This Error">
                      <IconButton
                        onClick={() => removeError(error.id)}
                        edge="end"
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body1">{error.message}</Typography>
                  <Stack sx={{ direction: "column", gap: 1 }}>
                    <Typography variant="body2">
                      Type: {error.type || "unknown"}
                    </Typography>
                    <Typography variant="body2">
                      {formatTimestamp(error.timestamp)}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
